import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { safeLocalGet, safeLocalSet, safeJsonParse } from '@/utils/ssr';

interface CompareCar {
  id: string;
  slug: string;
  make: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  fuel_type: string;
  transmission: string;
  engine_power?: number;
  images: string[];
  vehicle_type: string;
}

interface CompareContextType {
  compareList: CompareCar[];
  addToCompare: (car: CompareCar) => void;
  removeFromCompare: (id: string) => void;
  clearCompare: () => void;
  isInCompare: (id: string) => boolean;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

const MAX_COMPARE_ITEMS = 4;
const STORAGE_KEY = 'roautomarket_compare';

export const CompareProvider = ({ children }: { children: ReactNode }) => {
  const { toast } = useToast();
  const [compareList, setCompareList] = useState<CompareCar[]>(() =>
    safeJsonParse<CompareCar[]>(safeLocalGet(STORAGE_KEY), [])
  );

  useEffect(() => {
    safeLocalSet(STORAGE_KEY, JSON.stringify(compareList));
  }, [compareList]);

  const addToCompare = (car: CompareCar) => {
    if (compareList.length >= MAX_COMPARE_ITEMS) {
      toast({
        title: "Limită atinsă",
        description: `Poți compara maxim ${MAX_COMPARE_ITEMS} vehicule simultan`,
        variant: "destructive",
      });
      return;
    }

    if (compareList.some(item => item.id === car.id)) {
      toast({
        title: "Deja adăugat",
        description: "Acest vehicul este deja în lista de comparare",
      });
      return;
    }

    setCompareList(prev => [...prev, car]);
    toast({
      title: "Adăugat la comparare",
      description: `${car.make} ${car.model} a fost adăugat la comparare`,
    });
  };

  const removeFromCompare = (id: string) => {
    setCompareList(prev => prev.filter(item => item.id !== id));
  };

  const clearCompare = () => {
    setCompareList([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isInCompare = (id: string) => {
    return compareList.some(item => item.id === id);
  };

  return (
    <CompareContext.Provider value={{ compareList, addToCompare, removeFromCompare, clearCompare, isInCompare }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within CompareProvider');
  }
  return context;
};
